// GateSelectionPopup.tsx - Removed Actions column with Select buttons
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './GateSelectionPopup.css';
import '../companyManagement/CompanyFormOverrides.css';
import { GateApiService } from '../gateManagement/apiService';
import { Gate, Region } from '../gateManagement/types';

interface GateSelectionPopupProps {
  isOpen: boolean;
  selectedGates: Gate[];
  selectedRegionId: number | null;
  getAccessToken: () => Promise<string>;
  onClose: () => void;
  onConfirm: (gates: Gate[]) => void;
  onStatusUpdate: (status: { type: 'success' | 'error' | null; message: string }) => void;
}

type SortField = 'gateNumber' | 'description' | 'isActive';
type SortDirection = 'asc' | 'desc';

export const GateSelectionPopup: React.FC<GateSelectionPopupProps> = ({
  isOpen,
  selectedGates,
  selectedRegionId,
  getAccessToken,
  onClose,
  onConfirm,
  onStatusUpdate
}) => {
  // State management
  const [region, setRegion] = useState<Region | null>(null);
  const [gates, setGates] = useState<Gate[]>([]);
  const [tempSelectedGates, setTempSelectedGates] = useState<Gate[]>([]);
  
  // Loading states
  const [isLoadingGates, setIsLoadingGates] = useState(false);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [viewFilter, setViewFilter] = useState<'all' | 'selected'>('all');
  
  // Sorting state
  const [sortField, setSortField] = useState<SortField>('gateNumber');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Selection state
  const [selectAll, setSelectAll] = useState(false);
  const [showHelp, setShowHelp] = useState(true);
  const [indeterminate, setIndeterminate] = useState(false);
  
  // API service
  const [apiService] = useState(() => new GateApiService(getAccessToken));

  // Search input ref
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Focus search input after drawer animation completes (Safari fix)
  useEffect(() => {
    if (isOpen && selectedRegionId) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, selectedRegionId]);

  // Initialize temp selection with current selected gates
  useEffect(() => {
    if (isOpen) {
      setTempSelectedGates([...selectedGates]);
    }
  }, [isOpen, selectedGates]);

  // Load gates for the selected region
  const loadGates = useCallback(async (regionId: number) => {
    setIsLoadingGates(true);
    try {
      const result = await apiService.loadGates(regionId);
      if (result.success && result.data) {
        setGates(result.data);
        
        // Load region info for display
        const regionResult = await apiService.loadRegions();
        if (regionResult.success && regionResult.data) {
          const selectedRegion = regionResult.data.find(r => r.regionId === regionId);
          if (selectedRegion) {
            setRegion(selectedRegion);
          }
        }
      } else {
        onStatusUpdate({
          type: 'error',
          message: result.message || 'Failed to load gates'
        });
      }
    } catch (error) {
      onStatusUpdate({
        type: 'error',
        message: 'Error loading gates'
      });
    } finally {
      setIsLoadingGates(false);
    }
  }, [apiService, onStatusUpdate]);

  // Load data when popup opens and region is selected
  useEffect(() => {
    if (isOpen && selectedRegionId) {
      loadGates(selectedRegionId);
      setSearchTerm('');
      setSortField('gateNumber');
      setSortDirection('asc');
      setViewFilter('all');
    }
  }, [isOpen, selectedRegionId, loadGates]);

  // Filtered and sorted gates
  const processedGates = useMemo(() => {
    let filtered = gates.filter(gate => {
      // Only show active gates
      if (!gate.isActive) return false;
      
      // View filter - show all or only selected
      if (viewFilter === 'selected') {
        const isSelected = tempSelectedGates.some(g => g.gateId === gate.gateId);
        if (!isSelected) return false;
      }
      
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          gate.gateNumber.toLowerCase().includes(search) ||
          (gate.description && gate.description.toLowerCase().includes(search))
        );
      }
      
      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      let aValue: string | boolean = sortField === 'description' ? (a.description ?? '') : a[sortField];
      let bValue: string | boolean = sortField === 'description' ? (b.description ?? '') : b[sortField];
      
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [gates, searchTerm, viewFilter, sortField, sortDirection, tempSelectedGates]);

  // Update select all state based on current selection
  useEffect(() => {
    const selectedCount = tempSelectedGates.filter(gate => 
      processedGates.some(pg => pg.gateId === gate.gateId)
    ).length;
    
    setSelectAll(selectedCount === processedGates.length && processedGates.length > 0);
    setIndeterminate(selectedCount > 0 && selectedCount < processedGates.length);
  }, [tempSelectedGates, processedGates]);

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle row selection
  const handleRowSelect = (gate: Gate, event?: React.SyntheticEvent) => {
    if (event) {
      event.stopPropagation();
    }
    
    setTempSelectedGates(prev => {
      const isSelected = prev.some(g => g.gateId === gate.gateId);
      if (isSelected) {
        return prev.filter(g => g.gateId !== gate.gateId);
      } else {
        return [...prev, gate];
      }
    });
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll || indeterminate) {
      // Deselect all visible gates
      setTempSelectedGates(prev => 
        prev.filter(gate => !processedGates.some(pg => pg.gateId === gate.gateId))
      );
    } else {
      // Select all visible gates
      setTempSelectedGates(prev => {
        const newSelection = [...prev];
        processedGates.forEach(gate => {
          if (!newSelection.some(g => g.gateId === gate.gateId)) {
            newSelection.push(gate);
          }
        });
        return newSelection;
      });
    }
  };

  // Handle confirm selection
  const handleConfirm = () => {
    onConfirm(tempSelectedGates);
    onClose();
  };

  // Handle cancel
  const handleCancel = () => {
    setTempSelectedGates([...selectedGates]);
    onClose();
  };

  // Check if gate is selected
  const isGateSelected = (gate: Gate) => {
    return tempSelectedGates.some(g => g.gateId === gate.gateId);
  };

  if (!isOpen) return null;

  // Show error if no region is selected
  if (!selectedRegionId) {
    return (
      <div className="modal-overlay" onClick={handleCancel}>
        <div className="modal-content gate-selection-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Select Gate / Access Area</h3>
            <button onClick={handleCancel} className="close-modal-btn">×</button>
          </div>
          <div className="modal-body">
            <div className="empty-state error-state">
              <div className="empty-icon">⚠️</div>
              <h4>No Region Selected</h4>
              <p>Please select a region in the form first before choosing gates.</p>
            </div>
          </div>
          <div className="modal-footer">
            <button onClick={handleCancel} className="btn btn-secondary">Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="modal-content gate-selection-modal enterprise-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-section">
            <span className="modal-title-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </span>
            <h3>Select Gate / Access Area</h3>
            {region && (
              <span className="region-badge">{region.regionName} · {region.regionCode}</span>
            )}
          </div>
          <button onClick={handleCancel} className="close-modal-btn" title="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Search and Filter Bar */}
        <div className="modal-search-bar">
          <div className="search-filter-bar">
            <div className="search-input-container">
              <svg className="search-icon-svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search by gate number or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
                autoComplete="off"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="clear-search-btn" title="Clear">×</button>
              )}
            </div>
            <select
              value={viewFilter}
              onChange={(e) => setViewFilter(e.target.value as any)}
              className="status-filter"
            >
              <option value="all">All Gates</option>
              <option value="selected">Selected only</option>
            </select>
          </div>
        </div>

        <div className="modal-body">
          {/* Table with 3 columns - removed Actions column */}
          <div className="table-container">
            {isLoadingGates ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <span>Loading gates...</span>
              </div>
            ) : processedGates.length > 0 ? (
              <div className={`table-scroll-container ${processedGates.length > 10 ? 'large' : ''}`}>
                <table className="gates-table">
                  <thead>
                    <tr>
                      <th className="checkbox-column">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          ref={(el) => {
                            if (el) el.indeterminate = indeterminate;
                          }}
                          onChange={handleSelectAll}
                          className="select-all-checkbox"
                        />
                      </th>
                      <th 
                        className={`sortable ${sortField === 'gateNumber' ? 'sorted' : ''}`}
                        onClick={() => handleSort('gateNumber')}
                      >
                        Gate
                        {sortField === 'gateNumber' && (
                          <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                        )}
                      </th>
                      <th>Access Codes</th>
                      <th 
                        className={`sortable ${sortField === 'description' ? 'sorted' : ''}`}
                        onClick={() => handleSort('description')}
                      >
                        Description
                        {sortField === 'description' && (
                          <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {processedGates.map((gate) => {
                      const selected = isGateSelected(gate);
                      const gateAny = gate as any;
                      const codes: string[] = gateAny.accessCodes
                        ? gateAny.accessCodes.split(',').map((c: string) => c.trim()).filter(Boolean)
                        : [];
                      return (
                        <tr
                          key={gate.gateId}
                          className={`gate-row ${selected ? 'selected' : ''} ${gate.isActive ? 'active' : 'inactive'}`}
                          onClick={() => handleRowSelect(gate)}
                        >
                          <td className="checkbox-column">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={(e) => handleRowSelect(gate, e)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td className="gate-number">{gate.gateNumber}</td>
                          <td className="gate-access-codes">
                            {codes.length > 0 ? (
                              <div className="access-codes-list">
                                {codes.map((code, i) => (
                                  <span key={i} className="access-code-pill">{code}</span>
                                ))}
                              </div>
                            ) : (
                              <span className="no-description">—</span>
                            )}
                          </td>
                          <td className="gate-description">
                            {gate.description || <span className="no-description">No description</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : gates.length === 0 && !isLoadingGates ? (
              <div className="empty-state">
                <div className="empty-icon">🚪</div>
                <h4>No Gates Available</h4>
                <p>
                  {region 
                    ? `No gates found in ${region.regionName}. Contact your administrator to add gates for this region.`
                    : 'No gates available in the selected region.'
                  }
                </p>
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">🔍</div>
                <h4>No Gates Found</h4>
                <p>
                  {viewFilter === 'selected' 
                    ? 'No selected gates found'
                    : searchTerm 
                      ? `No gates match "${searchTerm}"`
                      : 'No gates found'
                  }
                </p>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="btn btn-primary btn-sm"
                  >
                    Clear Search
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <div className="footer-info">
            {tempSelectedGates.length > 0 ? (
              <span className="selection-count has-selection">
                {tempSelectedGates.length} gate{tempSelectedGates.length !== 1 ? 's' : ''} selected
              </span>
            ) : (
              <span className="selection-count">No gates selected</span>
            )}
          </div>
          <div className="footer-actions">
            <button onClick={handleCancel} className="btn btn-secondary">Cancel</button>
            <button
              onClick={handleConfirm}
              className="btn btn-primary"
              disabled={tempSelectedGates.length === 0}
            >
              Confirm Selection
            </button>
          </div>
        </div>
          </div>{/* end left column */}

          {/* Help Panel — animated */}
          <div style={{
            width: showHelp ? 280 : 38,
            minWidth: showHelp ? 280 : 38,
            background: '#fafbfc',
            borderLeft: '1px solid #e5e7eb',
            overflow: 'hidden',
            position: 'relative',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            transition: 'width 0.3s ease, min-width 0.3s ease'
          }}>
            <div
              onClick={() => setShowHelp(!showHelp)}
              title={showHelp ? 'Hide Guide' : 'Show Guide'}
              style={{
                position: 'absolute', top: 0, right: 0, bottom: 0, width: 32,
                background: '#f3f4f6', borderLeft: '1px solid #e5e7eb',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'flex-start', paddingTop: 14, gap: 6,
                cursor: 'pointer', color: '#9ca3af', zIndex: 5
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {showHelp
                  ? <polyline points="9 18 15 12 9 6"/>
                  : <polyline points="15 18 9 12 15 6"/>
                }
              </svg>
              <span style={{
                writingMode: 'vertical-rl', textOrientation: 'mixed',
                fontSize: 10, fontWeight: 600, letterSpacing: '0.02em',
                whiteSpace: 'nowrap', fontFamily: "'Segoe UI', sans-serif"
              }}>{showHelp ? 'Hide Guide' : 'Show Guide'}</span>
            </div>
            <div style={{
              padding: '20px 50px 20px 18px',
              overflowY: 'auto',
              boxSizing: 'border-box' as const,
              minWidth: 280,
              opacity: showHelp ? 1 : 0,
              transition: 'opacity 0.2s ease'
            }}>
              <div className="help-panel-card help-active">
                <h4 className="help-card-title">Select Gates</h4>
                <p className="help-card-description">Choose the gate entry points that personnel will use to access the facility. Only active gates in the selected region are shown.</p>
                <div className="help-card-tip">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F3694A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <span>Click any row or use the checkbox to select multiple gates.</span>
                </div>
              </div>
              <div className="help-panel-quickref">
                <h4 className="help-quickref-title">Quick Reference</h4>
                <ul className="help-quickref-list">
                  <li><span className="help-quickref-bullet"></span><span>Only active gates are displayed</span></li>
                  <li><span className="help-quickref-bullet"></span><span>Click column headers to sort</span></li>
                  <li><span className="help-quickref-bullet"></span><span>Use "Selected only" to review picks</span></li>
                  <li><span className="help-quickref-bullet"></span><span>Access codes are shown per gate</span></li>
                </ul>
              </div>
            </div>
          </div>
        </div>{/* end flex row */}
      </div>
    </div>
  );
};